//////////////////
// IMPORTS
//////////////////
// schema
import * as ISchema from "#webapp/src/__schema__";
// libraries
import { isEqual } from "lodash";
import * as React from "react";
import { connect } from "react-redux";
import { createFragmentContainer } from "react-relay";
// event-scheduler-suggested-times imports
import { eventSchedulerSuggestedTimesFragments } from "./EventSchedulerSuggestedTimes.gql";
import { IContainer, IProps, IState } from "./EventSchedulerSuggestedTimesTypes";
// other imports
import { CardButton } from "#webapp/src/components/card";
import { EventSuggestedTimesWarning } from "#webapp/src/components/event-suggested-times-warning";
import { PostMessageManager } from "#webapp/src/util/post-message.util";
import { slotsEqual } from "#webapp/src/util/scheduler.util";
import { getCurrentWorkingGroup } from "#webapp/src/util/working-group.util";
// components
import {
  NoSuggestedTimes,
  SuggestedTimeTileLarge,
} from "@clockwise/web-commons/src/components/suggested-times-row";
// state
import { IReduxState } from "#webapp/src/state/reducers/root.reducer";
// material-ui
import { CalloutBox } from "#webapp/src/components/callout-box";
import { CircularProgress } from "@clockwise/web-commons/src/components/circular-progress";
import { setHighlightedSlot } from "../../state/actions/planner-highlight.actions";
export type GetRoomIdSlot = {
  roomImpacts: Array<Pick<ISchema.ISchedulingRoomImpact, "roomId">>;
};
export const getRoomId = (slot: GetRoomIdSlot) => slot.roomImpacts?.[0]?.roomId;

export type GetRoomNameWorkingGroup = {
  rooms: Array<Pick<ISchema.IRoom, "roomId" | "name">>;
};
export const getRoomName = (workingGroup: GetRoomNameWorkingGroup | null, roomId: string) => {
  let roomName = "";

  if (roomId) {
    if (workingGroup) {
      roomName = workingGroup.rooms.find((room) => room.roomId === roomId)?.name || "";
    }
  }

  return roomName;
};

//////////////////
// COMPONENT
//////////////////
class EventSchedulerSuggestedTimesCmpt extends React.Component<IProps, IState> {
  constructor(props: IProps) {
    super(props);
  }

  // ~-~-~-~-~-~-~-
  // Handlers
  // ~-~-~-~-~-~-~-

  private onTimeSelection = (slot: ISchema.ISchedulingSlot, trackingIndex: number) => {
    const { slots } = this.props.schedulingResponse;

    if (this.props.onSelect && !slotsEqual(slot, this.props.selectedSlot)) {
      this.props.onSelect(slot, trackingIndex, slots);
    }
  };

  // ~-~-~-~-~-~-~-
  // Render
  // ~-~-~-~-~-~-~-
  private maybeRenderQualifierWarning = (qualifiers: ISchema.SchedulingResponseQualifier[]) => {
    const noTimesWithRoom =
      qualifiers.indexOf("NoConvenientTimesWithRooms" as ISchema.SchedulingResponseQualifier) !==
      -1;

    if (noTimesWithRoom) {
      return (
        <div>
          <CalloutBox style={{ marginBottom: 16 }} info>
            No suggestions. Try broadening your search to find times.
            <div>
              <CardButton
                label="Reset timeframe"
                onClick={() => this.props.resetTimeframe()}
                style={{ width: "unset", marginTop: 10, textAlign: "left" }}
              />
            </div>
          </CalloutBox>
        </div>
      );
    }

    return null;
  };

  private renderSlot = (slot: ISchema.ISchedulingSlot | undefined, index: number) => {
    const { slots } = this.props.schedulingResponse;
    const { persons, isLoading } = this.props;

    if (!slot) {
      return <span key={index} />;
    }

    const isSelected = slotsEqual(this.props.selectedSlot, slot);

    const trackingIndex = slots.findIndex((s) => isEqual(slot.timeRange, s.timeRange));

    const roomId = getRoomId(slot);
    const workingGroup = getCurrentWorkingGroup(this.props.org);
    const roomName = getRoomName(workingGroup, roomId);

    return (
      <SuggestedTimeTileLarge
        key={index}
        slot={slot}
        index={index}
        isLoading={isLoading}
        isSelected={!!isSelected}
        roomName={roomName}
        onSelect={() => {
          this.onTimeSelection(slot, trackingIndex);
          this.props.setHighlightedSlot(slot);
        }}
        people={persons}
        onMouseEnter={(hoveredSlot: ISchema.ISchedulingSlot) => {
          const durationMinutes =
            (hoveredSlot.timeRange.endTime.millis - hoveredSlot.timeRange.startTime.millis) /
            (1000 * 60);
          PostMessageManager.sendShowEventOnCalendar(
            hoveredSlot.timeRange.startTime.millis,
            durationMinutes,
            true,
          );
          this.props.setHighlightedSlot(slot);
        }}
        onMouseLeave={() => {
          PostMessageManager.sendShowEventOnCalendar(null, null, false);

          this.props.setHighlightedSlot(this.props.selectedSlot ?? null);
        }}
      />
    );
  };

  public componentWillUnmount() {
    PostMessageManager.sendShowEventOnCalendar(null, null, false);
  }

  public render() {
    const { slots, uncrawledCalendars, qualifiers } = this.props.schedulingResponse;

    if (this.props.isLoading) {
      return (
        <div className="cw-w-full cw-flex cw-items-center cw-justify-center">
          <CircularProgress />
        </div>
      );
    }

    if (!slots.length || slots.length < 1) {
      return (
        <NoSuggestedTimes
          dateIsInPast={false}
          firstAvailableDate={this.props.schedulingResponse?.queryRecommendation?.availableDate}
          currentDurationString={`${this.props.minutesDuration.toString()}m`}
          onSetDate={this.props.onSetDate}
        />
      );
    }

    return (
      <div>
        <EventSuggestedTimesWarning
          environment={this.props.environment}
          uncrawledCalendars={uncrawledCalendars}
          orgId={this.props.org.id}
        />
        {this.maybeRenderQualifierWarning(qualifiers)}
        {slots.map(this.renderSlot)}
      </div>
    );
  }
}

//////////////////
// REDUX
//////////////////
function mapStateToProps(_state: IReduxState, _ownProps: IContainer) {
  return {};
}

export const EventSchedulerSuggestedTimesRedux = connect(mapStateToProps, { setHighlightedSlot })(
  EventSchedulerSuggestedTimesCmpt,
);

//////////////////
// RELAY
//////////////////
export const EventSchedulerSuggestedTimes = createFragmentContainer<IContainer>(
  EventSchedulerSuggestedTimesRedux,
  eventSchedulerSuggestedTimesFragments,
);
